import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiCheck } from 'react-icons/fi';
import { CUSTOM_PRICING, normalizePlanTierId } from '../data/pricing';

import windowsIcon from "../../src/assets/images/windows.png"
import ubuntuIcon from "../../src/assets/images/ubuntu.png"
import debianIcon from "../../src/assets/images/debian.png"

import worldmap from '../../src/assets/images/world-map.png';
import usa from '../../src/assets/images/usa.png';
import nl from '../../src/assets/images/nl.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const ALLOWED_OS_NAMES = new Set([
  'Windows Server',
  'Debian 10',
  'Debian 11',
  'Debian 12',
  'Ubuntu 18.04',
  'Ubuntu 20.04',
  'Ubuntu 22.04',
  'Ubuntu 24.04'
]);

const getOsIcon = (name = '') => {
  const normalized = name.toLowerCase();
  if (normalized.includes('windows')) {
    return <img src={windowsIcon} alt="windows" className="w-10 h-10 object-contain" />;
  }
  if (normalized.includes('ubuntu')) {
    return <img src={ubuntuIcon} alt="ubuntu" className="w-10 h-10 object-contain" />;
  }
  if (normalized.includes('debian')) {
    return <img src={debianIcon} alt="debian" className="w-10 h-10 object-contain" />;
  }
  return <span className="text-3xl">💻</span>;
};

const normalizeTemplatesFromApi = (templates = []) => {
  return templates
    .map((template, index) => {
      const rawId = template?.id ?? template?.os_id ?? template?.template_id ?? template?.value;
      const id = rawId !== undefined && rawId !== null ? String(rawId) : '';
      const name = template?.name || template?.label || template?.title || '';
      if (!id || !name) return null;
      const normalizedName = String(name).trim();
      if (!ALLOWED_OS_NAMES.has(normalizedName)) return null;
      return { id, name: normalizedName };
    })
    .filter(Boolean);
};

const DURATION_STEPS = [30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360];

const DURATION_LABELS = {
  30: { label: 'Monthly', discount: 0 },
  90: { label: 'Quarterly', discount: 3 },
  180: { label: 'Half', discount: 4 },
  360: { label: 'Yearly', discount: 7 },
};

function formatDurationOption(days) {
  const info = DURATION_LABELS[days];
  if (!info) return `${days} days`;
  const off = info.discount > 0 ? ` — ${info.label}, ${info.discount}% off` : ` — ${info.label}`;
  return `${days} days${off}`;
}

const SPEC_ICONS = {
  cores: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/>
      <line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/>
      <line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/>
      <line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/>
      <line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/>
    </svg>
  ),
  ram: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="7" width="20" height="10" rx="2"/>
      <path d="M6 7V5M10 7V5M14 7V5M18 7V5M6 17v2M10 17v2M14 17v2M18 17v2"/>
    </svg>
  ),
  storage: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
    </svg>
  ),
  bandwidth: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M5 12.55a11 11 0 0 1 14.08 0"/>
      <path d="M10.54 16.1a6 6 0 0 1 2.92 0"/><line x1="12" y1="20" x2="12.01" y2="20"/>
    </svg>
  ),
  cpu: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
    </svg>
  ),
};

const OrderService = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(true);

  const selectedPlanTier = normalizePlanTierId(searchParams.get('plan'));
  const selectedLocationFromUrl = (searchParams.get('location') || 'us').toLowerCase();
  const selectedCustomPlan = CUSTOM_PRICING.tiers.find(t => t.id === selectedPlanTier);

  const [formData, setFormData] = useState({
    planTier: selectedPlanTier || '',
    location: selectedLocationFromUrl,
    osTemplate: '',
    duration: 30,
    customPrice: selectedCustomPlan
      ? (typeof selectedCustomPlan.price === 'object'
        ? selectedCustomPlan.price[selectedLocationFromUrl]
        : selectedCustomPlan.price)
      : 0
  });

  useEffect(() => {
    if (!selectedPlanTier || !selectedCustomPlan) {
      toast.error('Please select a plan first');
      navigate('/select-plan');
      return;
    }
    fetchConfig();
  }, [selectedPlanTier, selectedCustomPlan, navigate]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/servers/config`);
      const templates = normalizeTemplatesFromApi(response.data?.templates || []);
      setConfig({ ...response.data, templates });
      if (templates.length > 0) {
        setFormData(prev => ({ ...prev, osTemplate: templates[0].id }));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.osTemplate) { toast.error('Please select an operating system'); return; }
    if (!termsAccepted) { toast.error('Please accept the Terms of Service'); return; }
    setSubmitting(true);
    toast.loading('Provisioning your server, please wait (~60 seconds)...', { id: 'order-create' });
    try {
      const token = localStorage.getItem('token');
      const customPrice = typeof selectedCustomPlan.price === 'object'
        ? selectedCustomPlan.price[formData.location]
        : selectedCustomPlan.price;
      const response = await axios.post(
        `${API_URL}/servers/create`,
        {
          planTier: formData.planTier,
          planDisplayName: selectedCustomPlan?.name || formData.planTier,
          customPrice,
          location: formData.location,
          osTemplate: formData.osTemplate,
          duration: formData.duration
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 150000 }
      );
      toast.success('Server created successfully!', { id: 'order-create' });
      navigate(`/server/${response.data.server._id}`);
    } catch (error) {
      toast.dismiss('order-create');
      if (error.code === 'ECONNABORTED') {
        toast.error('Server creation is taking longer than expected. Please check your servers list.');
      } else {
        toast.error(error.response?.data?.message || error.message || 'Failed to create server.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getDiscount = (days) => {
    if (days >= 360) return 7;
    if (days >= 180) return 4;
    if (days >= 90) return 3;
    return 0;
  };

  const getBasePrice = () => {
    if (!selectedCustomPlan) return 0;
    return typeof selectedCustomPlan.price === 'object'
      ? selectedCustomPlan.price[formData.location]
      : selectedCustomPlan.price;
  };

  const calculatePrice = () => {
    const monthlyPrice = getBasePrice();
    const discount = getDiscount(formData.duration);
    const total = (monthlyPrice * formData.duration) / 30;
    return total * (1 - discount / 100);
  };

  const calculateOriginalPrice = () => {
    const monthlyPrice = getBasePrice();
    return (monthlyPrice * formData.duration) / 30;
  };

  const discountAmount = calculateOriginalPrice() - calculatePrice();
  const discount = getDiscount(formData.duration);

  if (loading || !selectedCustomPlan) {
    return (
      <Layout>
        <PageLoader />
      </Layout>
    );
  }

  return (
    <Layout>
      <style>{`
        .duration-btn {
          width: 100%;
          min-height: 44px;
          padding: 8px 6px;
          box-sizing: border-box;
          border-radius: 15px;
          border: 1px solid #EBEBEB47;
          background-color: #FFFFFF21;
          color: white;
          font-weight: 500;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.18s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          position: relative;
        }
        @media (min-width: 640px) {
          .duration-btn {
            font-size: 14px;
            padding: 10px 8px;
          }
        }
        .duration-btn.active {
          background: linear-gradient(to top, #3C3CFFC2, #3C3CFF00);
        }
        .duration-btn .radio-dot {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          border: 2px solid #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          transition: all 0.2s ease;
        }
        .duration-btn.active .radio-dot {
          background: rgba(59,130,246,0.2);
        }
        .duration-btn .radio-dot::after {
          content: "";
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: white;
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        .duration-btn.active .radio-dot::after {
          opacity: 1;
        }
        .os-btn {
          background-color: #FFFFFF21;
          border: 1.5px solid #EBEBEB47;
          border-radius: 14px;
          padding: 18px 12px;
          cursor: pointer;
          transition: all 0.18s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }
        .os-btn:hover {
          background: linear-gradient(to top, #3C3CFFC2, #3C3CFF00);
        }
        .os-btn.active {
          background: linear-gradient(to top, #3C3CFFC2, #3C3CFF00);
        }
        .spec-pill {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 5px 12px;
          font-size: 13px;
          font-weight: 500;
          white-space: nowrap;
          color: #D6D6D6;
        }
        .location-pin {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(255,255,255,0.10);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 12px;
          backdrop-filter: blur(6px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.4), inset 0 1px 1px rgba(255,255,255,0.23), inset 0 -16px 28px rgba(255,255,255,0.15);
          padding: 8px 14px;
          cursor: pointer;
          transition: all 0.18s ease;
          color: white;
          font-size: 15px;
          font-weight: 500;
          position: absolute;
          transform: translate(-50%, -50%);
        }
        .location-pin:hover {
          background: rgba(255,255,255,0.16);
        }
        .location-pin.active {
          border-color: rgba(96,165,250,0.6);
          background: rgba(59,130,246,0.2);
          box-shadow: 0 0 0 2px rgba(59,130,246,0.35), 0 2px 4px rgba(0,0,0,0.4);
        }
        .radio-outer {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .location-pin.active .radio-outer {
          border-color: #3b82f6;
        }
        .radio-inner {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #3b82f6;
        }
        .pay-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          box-sizing: border-box;
          background-color: #FFFFFF0F;
          border: none;
          border-radius: 12px;
          padding: 14px 20px;
          color: white;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: inset 0 1px 1px rgba(255,255,255,0.23), 0 2px 4px rgba(0,0,0,0.25), inset 0 -16px 28px rgba(255,255,255,0.15);
        }
        @media (min-width: 768px) {
          .pay-btn {
            padding: 14px 28px;
          }
        }
        .pay-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          transform: translateY(-1px);
          box-shadow: inset 0 1px 1px rgba(255,255,255,0.23), 0 10px 25px rgba(37,99,235,0.45), inset 0 -16px 28px rgba(255,255,255,0.15);
        }
        .pay-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .pay-btn .arrow-circle {
          width: 32px;
          height: 32px;
          background-color: #ffffff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .discount-badge {
          background: #0B3BF0;
          color: #CECECE;
          border-radius: 6px;
          padding: 2px 8px;
          font-size: 12px;
          font-weight: 600;
        }

        /* USA pin: fixed position */
        .pin-usa {
          top: 35%;
          left: 22%;
        }

        .pin-nl {
          top: 28%;
          left: 51%;
        }
        @media (max-width: 640px) {
          .pin-usa {
            top: 36%;
            left: 16%;
          }
          .pin-nl {
            top: 26%;
            left: 70%;
          }
        }
        .order-page-select {
          width: 100%;
          border-radius: 15px;
          border: 1px solid #EBEBEB47;
          background-color: #FFFFFF21;
          color: white;
          padding: 14px 44px 14px 18px;
          font-size: 15px;
          font-weight: 500;
          line-height: 1.35;
          min-height: 48px;
          outline: none;
          box-sizing: border-box;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' fill='none' stroke='%23cbd5e1' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5.5 7.5L10 12l4.5-4.5'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 14px center;
          background-size: 20px;
        }
        .order-page-select:focus {
          border-color: rgba(96, 165, 250, 0.55);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
        }
        .order-page-select option {
          background: #1a1f2e;
          color: #f1f5f9;
        }
      `}</style>

      <div className="-mx-4 w-[calc(100%+2rem)] max-w-none sm:-mx-6 sm:w-[calc(100%+3rem)] lg:-mx-8 lg:w-[calc(100%+4rem)] flex flex-col items-center justify-center min-h-[calc(100vh-120px)]">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="w-full rounded-2xl border border-[#EBEBEB1C] bg-[#FFFFFF14] px-5 pb-6 sm:px-6">

        {/* Header — Plan name + Spec Pills */}
        <div className="py-5 mb-5 flex flex-wrap items-center gap-3 justify-between border-b border-[#9C9C9C]">
          <h1 className="text-2xl text-white">{selectedCustomPlan?.name || 'Plan'}</h1>
          <div className="flex flex-wrap gap-2">
            <div className="spec-pill">
              <span className="flex items-center gap-2 p-2 rounded-lg bg-[#0B3BF0]">{SPEC_ICONS.cores}</span>
              {selectedCustomPlan.specs.cores} Cores
            </div>
            <div className="spec-pill">
              <span className="flex items-center gap-2 p-2 rounded-lg bg-[#0B3BF0]">{SPEC_ICONS.ram}</span>
              {selectedCustomPlan.specs.ram} GB DDR5 RAM
            </div>
            <div className="spec-pill">
              <span className="flex items-center gap-2 p-2 rounded-lg bg-[#0B3BF0]">{SPEC_ICONS.storage}</span>
              {selectedCustomPlan.specs.storage} GB NVMe
            </div>
            <div className="spec-pill">
              <span className="flex items-center gap-2 p-2 rounded-lg bg-[#0B3BF0]">{SPEC_ICONS.bandwidth}</span>
              {selectedCustomPlan.specs.bandwidth} Uplink
            </div>
            <div className="spec-pill">
              <span className="flex items-center gap-2 p-2 rounded-lg bg-[#0B3BF0]">{SPEC_ICONS.cpu}</span>
              {selectedCustomPlan.specs.cpu}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Duration — dropdown on mobile, grid from md+ */}
          <div className="w-full">
            <h2 className="text-2xl font-light mb-5">Duration</h2>
            <div className="md:hidden mb-1">
              <label htmlFor="order-duration" className="sr-only">
                Select duration in days
              </label>
              <select
                id="order-duration"
                className="order-page-select"
                value={formData.duration}
                onChange={(e) =>
                  setFormData({ ...formData, duration: Number(e.target.value) })
                }
              >
                {DURATION_STEPS.map((days) => (
                  <option key={days} value={days}>
                    {formatDurationOption(days)}
                  </option>
                ))}
              </select>
            </div>
            <div className="hidden md:grid w-full grid-cols-6 sm:grid-cols-12 gap-x-2 gap-y-4 sm:gap-x-3">
              {DURATION_STEPS.map(days => {
                const isActive = formData.duration === days;
                const labelInfo = DURATION_LABELS[days];
                return (
                  <div key={days} className="flex min-w-0 flex-col items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, duration: days })}
                      className={`duration-btn ${isActive ? 'active' : ''}`}
                    >
                      {days}
                      {isActive && <span className="radio-dot" />}
                    </button>
                    {labelInfo && (
                      <div className="flex flex-col items-center text-center">
                        <span className="text-[10px] sm:text-xs text-white/50 leading-tight">{labelInfo.label}</span>
                        {labelInfo.discount > 0 && (
                          <span className="text-[10px] sm:text-xs bg-[#0B3BF0] text-[#CECECE] px-1 py-0.5 rounded-md mt-0.5">{labelInfo.discount}% Off</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Location + OS — side by side */}
          <div className="grid lg:grid-cols-2 gap-5">

            {/* Location */}
            <div>
              <h2 className="text-2xl font-light">Location</h2>
              <div className="relative w-full">
                <img
                  src={worldmap}
                  alt="world map"
                  className="w-full object-contain opacity-80"
                />

                {/* USA + NL pins on map */}
                <div
                  className={`location-pin pin-usa flex ${formData.location === 'us' ? 'active' : ''}`}
                  onClick={() => {
                    const newPrice = typeof selectedCustomPlan.price === 'object'
                      ? selectedCustomPlan.price['us']
                      : selectedCustomPlan.price;
                    setFormData({ ...formData, location: 'us', customPrice: newPrice });
                  }}
                >
                  <div className="rounded-full border border-white/30 overflow-hidden w-6 h-6 flex-shrink-0">
                    <img src={usa} alt="usa" className="w-full h-full object-cover" />
                  </div>
                  <span>USA</span>
                  <div className="radio-outer">
                    {formData.location === 'us' && <div className="radio-inner" />}
                  </div>
                </div>

                <div
                  className={`location-pin pin-nl flex ${formData.location === 'nl' ? 'active' : ''}`}
                  onClick={() => {
                    const newPrice = typeof selectedCustomPlan.price === 'object'
                      ? selectedCustomPlan.price['nl']
                      : selectedCustomPlan.price;
                    setFormData({ ...formData, location: 'nl', customPrice: newPrice });
                  }}
                >
                  <div className="rounded-full border border-white/30 overflow-hidden w-6 h-6 flex-shrink-0">
                    <img src={nl} alt="nl" className="w-full h-full object-cover" />
                  </div>
                  <span>NL</span>
                  <div className="radio-outer">
                    {formData.location === 'nl' && <div className="radio-inner" />}
                  </div>
                </div>
              </div>
            </div>

            {/* Operating System — dropdown on mobile, grid from md+ */}
            <div className="mb-8 lg:mb-0">
              <h2 className="text-2xl font-light mb-3">Operating System</h2>
              {config?.templates?.length > 0 ? (
                <>
                  <div className="md:hidden">
                    <label htmlFor="order-os" className="sr-only">
                      Select operating system
                    </label>
                    <select
                      id="order-os"
                      className="order-page-select"
                      value={formData.osTemplate}
                      onChange={(e) =>
                        setFormData({ ...formData, osTemplate: e.target.value })
                      }
                    >
                      {config.templates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="hidden md:grid grid-cols-3 gap-3">
                    {config.templates.map(template => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, osTemplate: template.id })}
                        className={`os-btn ${formData.osTemplate === template.id ? 'active' : ''}`}
                      >
                        {getOsIcon(template.name)}
                        <span className="font-light text-center tracking-wide text-white">
                          {template.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="p-4 border border-yellow-700/50 bg-yellow-900/15 text-yellow-200/80 rounded-xl text-sm">
                  No operating systems available at the moment. Please try again shortly.
                </div>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Bottom Bar — mobile: pay full-width on top, terms below; md+: terms left, pay right */}
      <div className="w-full flex flex-col gap-4 px-5 py-4 sm:px-6 md:flex-row md:items-center md:justify-between md:gap-3 bg-[#FFFFFF14] border border-[#EBEBEB1C] rounded-2xl mt-6">

        {/* Price + Pay */}
        <div className="order-1 flex w-full flex-col gap-3 md:order-2 md:w-auto md:shrink-0 md:items-end">
          {discount > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-2 md:justify-end">
              <span className="text-white/50 text-sm">-€{discountAmount.toFixed(2)}</span>
              <span className="discount-badge">{discount}% Off</span>
              <span className="text-white/50 text-sm">Amount</span>
              <span className="text-white font-bold text-lg">€{calculateOriginalPrice().toFixed(2)}</span>
            </div>
          )}

          <button
            type="button"
            disabled={submitting || !termsAccepted || (user?.balance !== undefined && user.balance < calculatePrice())}
            className="pay-btn w-full justify-between md:w-auto md:justify-start md:gap-3"
            onClick={handleSubmit}
          >
            <span className="shrink-0">Pay Total</span>
            <span className="flex items-center gap-3 shrink-0">
              <span className="text-xl font-bold tabular-nums">€{calculatePrice().toFixed(2)}</span>
              <div className="arrow-circle">
                <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0.75 10.75L5.75 5.75L0.75 0.75" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
            </span>
          </button>
        </div>

        {/* Terms */}
        <div className="order-2 flex w-full flex-col gap-1 md:order-1 md:min-w-0 md:flex-1">
          <label className="flex cursor-pointer items-start gap-2.5 md:items-center">
            <div
              onClick={() => setTermsAccepted(!termsAccepted)}
              className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 md:mt-0"
              style={{ borderColor: '#FFFFFF', background: termsAccepted ? 'rgba(59,130,246,0.2)' : 'transparent' }}
            >
              {termsAccepted && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
            </div>
            <span className="text-white">
              Accept the{' '}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#486FFF] underline underline-offset-2 hover:text-[#7c9cff]"
                onClick={(e) => e.stopPropagation()}
              >
                Terms of Service
              </Link>
              {' '}&amp; the{' '}
              <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#486FFF] underline underline-offset-2 hover:text-[#7c9cff]"
                onClick={(e) => e.stopPropagation()}
              >
                Privacy Statement
              </Link>
            </span>
          </label>
          <p className="text-xs text-white ml-7 max-w-2xl">
            I confirm that I consent to the contract being carried out prior to the expiration of the withdrawal
            period. I am aware that once the execution of the contract has started, I will lose my right to cancel
            or withdraw from the agreement.
          </p>
        </div>
        </div>
        </div>
      </div>

      {user?.balance !== undefined && user.balance < calculatePrice() && (
        <div className="w-full text-center text-sm text-red-400 mt-4 pb-2">
          Insufficient balance.{' '}
          <a href="/topup" className="text-red-300 underline">Top up now</a>
        </div>
      )}
    </Layout>
  );
};

export default OrderService;